import {Injectable} from '@angular/core';
import {Router} from "@angular/router";
import {HttpBackend, HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {Observable, Subject, catchError, finalize, firstValueFrom, from, map, of, shareReplay, tap, throwError, timeout} from "rxjs";
import {environment} from '../../environments/environment';
import {
    AuthUser,
    MagicTokenResponse,
    ProvidersResponse,
    SSOLoginContent,
    SSOEmailVerification,
    SSOEmailVerificationResponse,
    TwoFaResponse,
    UsernameSearch
} from '../_types/auth';
import {User} from '../_types/user';
import {GenericResponse, GenericResponseWithPagination} from '../_types/shared';
import {CommentFlare, ProfileQuestion, Question, QuestionAnswer} from '../_types/communities';
import {TimeoutStateService} from "./timeout-state.service";
import {RecruitmentTrackingService} from "./recruitment-tracking.service";

interface LoginResponse {
    token: string;
    refresh: string;
    email: string;
    id: number;
    user: User;
}

interface NativeRefreshHandler {
    postMessage(body: {staleToken: string}): Promise<string>;
}

interface AndroidRefreshHandler {
    postMessage(body: string): void;
    onmessage: ((event: {data: string}) => void) | null;
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    interval: any;
    private refreshOperation?: Observable<{access: string; refresh: string}>;
    private appRefreshOperation?: Observable<{access: string}>;
    private loginAccessToken?: string;
    pendingMfaUser?: AuthUser;
    pendingSSOEmailVerification?: SSOEmailVerification;
    completedSSOEmailLogin?: AuthUser;
    private user: User | null = null;
    private storeClearedSubject: Subject<void> = new Subject<void>();
    public storeCleared$: Observable<void> = this.storeClearedSubject.asObservable();

    constructor(
        private router: Router,
        private timeoutStateService: TimeoutStateService,
        private recruitmentTrackingService: RecruitmentTrackingService,
        private http: HttpClient,
        private httpBackend: HttpBackend) {
    }

    disableTimeout(milliseconds: number): void {
        let that: this = this;
        // Refresh the user every minute;
        this.interval = setInterval(function (): void {
            void that.refreshUser().catch(() => {});
        }, 59999);

        // Cancel the timeout after the max time allowed
        setInterval(function (): void {
            that.stopTimeoutDisable();
        }, milliseconds);
    }

    stopTimeoutDisable(): void {
        clearInterval(this.interval)
    }

    login(body: {
        email: string,
        password: string,
        referrer?: string,
        community_id?: number,
        keep_logged_in?: boolean
    }): Observable<GenericResponse<AuthUser>> {
        localStorage.removeItem('hijacked');
        return this.http.post<GenericResponse<AuthUser>>(`${environment.apiUrl}/users/login/`, body).pipe(tap(r => this.storeLogin(r.data)));
    }

    request2fa(authToken: string, community_id?: number): Observable<TwoFaResponse> {
        const httpOptions = {
            headers: new HttpHeaders({'Authorization': 'Bearer ' + authToken})
        };
        return this.http.post<TwoFaResponse>(`${environment.apiUrl}/users/mfa/request-code/`, {community_id}, httpOptions);
    }

    verify2fa(body: any, authToken: string): Observable<{
        verified: boolean,
        session_token: string
    }> {
        const httpOptions = {
            headers: new HttpHeaders({'Authorization': 'Bearer ' + authToken})
        };
        return this.http.post<{
            verified: boolean,
            session_token: string
        }>(`${environment.apiUrl}/users/mfa/verify/`, body, httpOptions).pipe(tap(r => {
            if (r.verified) {
                localStorage.removeItem('mfa_pending');
                this.pendingMfaUser = undefined;
            }
        }));
    }

    // After login save token and other values(if any) in localStorage
    setUser(resp: LoginResponse): void {
        if (resp.token !== this.loginAccessToken) {
            this.setTokens(resp.token, resp.refresh);
            this.loginAccessToken = resp.token;
        }
        localStorage.setItem('currentUser', JSON.stringify(resp.user));
    }

    setTokens(access: string, refresh?: string): void {
        localStorage.setItem('access_token', access);
        if (refresh) {
            localStorage.setItem('refresh_token', refresh);
        } else {
            localStorage.removeItem('refresh_token');
        }
    }

    private storeLogin(data: AuthUser): void {
        if (!data?.token) return;
        this.setTokens(data.token, data.refresh);
        this.loginAccessToken = data.token;
        this.pendingMfaUser = data.mfa_required ? data : undefined;
        if (data.mfa_required) localStorage.setItem('mfa_pending', 'true');
        else localStorage.removeItem('mfa_pending');
    }

    refreshTokens(): Observable<{access: string; refresh: string}> {
        if (this.refreshOperation) return this.refreshOperation;
        const refresh = localStorage.getItem('refresh_token');
        if (!refresh) {
            this.logout();
            return throwError(() => new Error('Please sign in again.'));
        }
        // Refresh tokens are single-use. Serialize rotation across tabs as well as
        // requests in this tab, then read storage again after acquiring the lock.
        const access = localStorage.getItem('access_token');
        const renew = (): Observable<{access: string; refresh: string}> => {
            const currentRefresh = localStorage.getItem('refresh_token');
            const currentAccess = localStorage.getItem('access_token');
            if (currentRefresh !== refresh) {
                if (currentRefresh && currentAccess && this.sameSession(access, currentAccess)) {
                    return of({access: currentAccess, refresh: currentRefresh});
                }
                return throwError(() => new Error('The authentication session changed.'));
            }
            return this.requestTokenRefresh(refresh, access);
        };
        const operation = navigator.locks?.request
            ? from(navigator.locks.request('explain-token-refresh', () => firstValueFrom(renew())))
            : renew();
        this.refreshOperation = operation.pipe(
            finalize(() => this.refreshOperation = undefined),
            shareReplay({bufferSize: 1, refCount: false})
        );
        return this.refreshOperation;
    }

    sameSession(previous: string | null, current: string): boolean {
        if (!previous) return false;
        try {
            const claims = (token: string): {session_id?: string} => JSON.parse(atob(
                token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
            ));
            const session = claims(previous).session_id;
            return !!session && session === claims(current).session_id;
        } catch {
            return false;
        }
    }

    private requestTokenRefresh(refresh: string, access: string | null): Observable<{access: string; refresh: string}> {
        // Bypass interceptors so a failed refresh cannot recursively refresh.
        return new HttpClient(this.httpBackend).post<{access: string; refresh: string}>(
            `${environment.apiUrl}/token/refresh/`, {refresh}
        ).pipe(
            // A stalled request must not hold the shared refresh lock indefinitely.
            timeout(30_000),
            tap(pair => {
                if (!pair.access || !pair.refresh || localStorage.getItem('refresh_token') !== refresh) {
                    throw new Error('The authentication session changed.');
                }
                this.setTokens(pair.access, pair.refresh);
            }),
            catchError(error => {
                if (localStorage.getItem('refresh_token') !== refresh) {
                    const currentAccess = localStorage.getItem('access_token');
                    const currentRefresh = localStorage.getItem('refresh_token');
                    if (currentAccess && currentRefresh && this.sameSession(access, currentAccess)) {
                        return of({access: currentAccess, refresh: currentRefresh});
                    }
                    return throwError(() => new Error('The authentication session changed.'));
                }
                // Network errors, rate limits and server failures do not invalidate
                // the session. Keep the editor and credentials so the user can retry.
                if ([401, 403].includes(error.status) && localStorage.getItem('refresh_token') === refresh) this.logout();
                return throwError(() => error);
            })
        );
    }

    /** Requests a new access token from the mobile app without exposing its refresh token. */
    refreshAppToken(staleToken: string): Observable<{access: string}> {
        if (this.appRefreshOperation) return this.appRefreshOperation;
        const appWindow = window as Window & {
            webkit?: {messageHandlers?: {authRefresh?: NativeRefreshHandler}};
            AndroidAuthRefresh?: AndroidRefreshHandler;
        };
        const ios = appWindow.webkit?.messageHandlers?.authRefresh;
        const android = appWindow.AndroidAuthRefresh;
        // Older mobile builds have no bridge; retain their existing refresh behavior.
        if (!ios && !android) return this.refreshTokens();
        const request = ios ? ios.postMessage({staleToken}) : new Promise<string>((resolve, reject) => {
            android!.onmessage = event => {
                android!.onmessage = null;
                const response = JSON.parse(event.data) as {access?: string; error?: string};
                if (response.access) resolve(response.access);
                else reject(new Error(response.error || 'Unable to renew session.'));
            };
            android!.postMessage(JSON.stringify({staleToken}));
        });
        this.appRefreshOperation = from(request).pipe(
            tap(access => {
                const current = localStorage.getItem('access_token');
                if (current !== staleToken && current !== access) {
                    throw new Error('The authentication session changed.');
                }
                this.setTokens(access);
            }),
            map(access => ({access})),
            finalize(() => this.appRefreshOperation = undefined),
            shareReplay({bufferSize: 1, refCount: false})
        );
        return this.appRefreshOperation;
    }

    signOut(): void {
        this.http.post(`${environment.apiUrl}/users/logout/`, {
            refresh: localStorage.getItem('refresh_token')
        }).pipe(finalize(() => this.logout())).subscribe({error: () => {}});
    }

    patchUser(body: any, file?: any): Observable<any> {
        const headerAttachmentNameValue: string = 'attachment; filename="profile_picture"';
        const headers = {
            'Content-Disposition': headerAttachmentNameValue,
        };
        const fd: FormData = new FormData();

        for (const [key, value] of Object.entries(body)) {
            if (typeof value == 'string') {
                fd.append(key, value);
            } else {
                fd.append(key, JSON.stringify(value));
            }
        }

        if (file) {
            fd.append('profile_picture', file);
        }
        return this.http.patch<any>(`${environment.apiUrl}/users/`, fd, {
            headers
        });
    }

    // Checking if token is set
    isLoggedIn(): boolean {
        return localStorage.getItem('access_token') != null && localStorage.getItem('mfa_pending') !== 'true';
    }

    // After clearing localStorage redirect to login screen
    logout(returnUrl?: string): void {
        this.clearStore()
        this.user = null;
        if (returnUrl) {
            this.router.navigate(['/login'], {queryParams: {returnUrl: returnUrl}});
        } else {
            this.router.navigate(['/login']);
        }
    }

    clearStore(): void {
        this.timeoutStateService.clearTimeoutState();
        localStorage.removeItem('hijacked');
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('mfa_pending');
        this.pendingMfaUser = undefined;
        this.pendingSSOEmailVerification = undefined;
        this.completedSSOEmailLogin = undefined;
        this.loginAccessToken = undefined;
        this.user = null;
        localStorage.removeItem('currentUser');
        localStorage.removeItem('SSOUser');
        this.storeClearedSubject.next();
    }

    getUser(): any {
        return this.user ?? JSON.parse(localStorage.getItem('currentUser') as string);
    }

    async refreshUser(): Promise<User | null> {
        return new Promise((resolve, reject): void => {
            this.http.get<any>(`${environment.apiUrl}/users/`).subscribe(r => {
                localStorage.setItem('currentUser', JSON.stringify(r));
                resolve(r)
            }, e => {
                reject(null)
                console.error(e);
            });
        });
    }

    isSuperUser(): boolean {
        return this.getUser()?.['is_superuser'];
    }

    isStaff(): boolean {
        return this.getUser()?.['is_staff'];
    }

    requestPasswordReset(email: string, community_id?: number): Observable<User> {
        localStorage.removeItem('hijacked');
        return this.http.post<User>(`${environment.apiUrl}/users/request-password-reset/`, {email, community_id});
    }

    resetPassword(token: string, body: any): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/users/reset-password/${token}/`, body);
    }

    createUser(body: any, file?: any, inviteToken?: string): Observable<any> {
        let params = new HttpParams();
        if (inviteToken) {
            params = params.append('invite_token', inviteToken);
        }

        const headerAttachmentNameValue: string = 'attachment; filename="profile_picture"';
        const headers = {
            'Content-Disposition': headerAttachmentNameValue,
        };
        const fd: FormData = new FormData();

        const requestBody: any = this.withExternalRecruitmentId(body);
        for (const [key, value] of Object.entries(requestBody)) {
            if (typeof value == 'string') {
                fd.append(key, value);
            } else {
                fd.append(key, JSON.stringify(value));
            }
        }

        if (file) {
            fd.append('profile_picture', file);
        }
        return this.http.post<any>(`${environment.apiUrl}/users/`, fd, {
            headers, params
        }).pipe(tap((response): void => {
            this.recruitmentTrackingService.clear();
            this.storeLogin(response.data);
        }));
    }

    getCommunityOnboardingQuestions(id: number): Observable<GenericResponseWithPagination<Question>> {
        return this.http.get<GenericResponseWithPagination<Question>>(`${environment.apiUrl}/communities/${id}/questions/`);
    }

    checkEligibility(id: number, body: QuestionAnswer[]): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/communities/${id}/onboarding/`, {answers: body});
    }

    addUserToCommunity(community_id: number, token?: string): Observable<any> {
        let headers: HttpHeaders = new HttpHeaders({'Authorization': 'Bearer ' + token});
        return this.http.post<any>(`${environment.apiUrl}/users/add-to-community/`, {
            community_id: community_id,
            ...this.withExternalRecruitmentId({}),
        }, {headers}).pipe(tap((): void => this.recruitmentTrackingService.clear()));
    }

    getUserInvite(invitetoken: string): Observable<any> {
        return this.http.get<any>(`${environment.apiUrl}/invites/accept/${invitetoken}/`);
    }

    async setUserWithToken(token: string, hijacked: boolean = false, refresh?: string): Promise<unknown> {
        return new Promise((resolve, reject): void => {
            this.setTokens(token, refresh);
            localStorage.removeItem('mfa_pending');
            this.http.get<any>(`${environment.apiUrl}/users/`).subscribe(r => {
                r.hijacked = hijacked
                localStorage.setItem('currentUser', JSON.stringify(r));
                resolve(r)
            }, e => {
                reject(e)
                console.error(e);
            });
        });
        // Use token received from passthrough page to set the user.
    }


    loginWithMagicToken(token: string): Observable<MagicTokenResponse> {
        return this.http.post<MagicTokenResponse>(`${environment.apiUrl}/users/mfa/magic-link/`, {
            token
        });
    }

    leaveCommunity(community_id: number): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/communities/${community_id}/unsubscribe/`, {});
    }

    // profile Questions
    getCommunityProfileQuestions(id?: number): Observable<ProfileQuestion[]> {
        let params: HttpParams = new HttpParams();
        if (id) {
            params = params.append('community_id', id);
        }
        return this.http.get<ProfileQuestion[]>(`${environment.apiUrl}/profile-questions`, {
            params
        });
    }

    getProfileQuestionAnswer(id: number): Observable<any> {
        let params: HttpParams = new HttpParams();
        params = params.append('community_id', id);
        return this.http.get<any>(`${environment.apiUrl}/profile-answers`, {
            params
        });
    }

    postProfileQuestionAnswer(body: any): Observable<{
        "count": number,
        "next": string
        "previous": string,
        "results": CommentFlare[]
    }> {
        return this.http.post<any>(`${environment.apiUrl}/profile-answers`, body);
    }

    //   SSO

    getCommunitySSOProviders(id: number): Observable<GenericResponse<ProvidersResponse>> {
        let params: HttpParams = new HttpParams();
        params = params.append('community_id', id);
        return this.http.get<GenericResponse<ProvidersResponse>>(`${environment.apiUrl}/sso/providers/`, {
            params
        });
    }

    SSOLoginRedirect(provider: 'google' | 'microsoft' | 'apple' | 'slack' | 'facebook', community_id: number, keep_logged_in: boolean): Observable<GenericResponse<{
        url: string,
        state: string
    }>> {
        let params: HttpParams = new HttpParams();
        return this.http.post<GenericResponse<{
            url: string,
            state: string
        }>>(`${environment.apiUrl}/sso/authorize/`, {provider, community_id, keep_logged_in}, {
            params
        });
    }

    SSOLogin(body: SSOLoginContent, keepLoggedIn?: boolean, inviteToken?: string): Observable<GenericResponse<AuthUser | SSOEmailVerificationResponse>> {
        let params: HttpParams = new HttpParams();
        if (keepLoggedIn) {
            params = params.append('keepLoggedIn', keepLoggedIn)
        }
        if (inviteToken) {
            params = params.append('inviteToken', inviteToken)
        }
        return this.http.post<GenericResponse<AuthUser | SSOEmailVerificationResponse>>(`${environment.apiUrl}/users/login/sso/`, body, {
            params
        }).pipe(tap(r => {
            if ('email_verification' in r.data) {
                this.clearStore();
                this.pendingSSOEmailVerification = r.data.email_verification;
            } else {
                this.storeLogin(r.data);
            }
        }));
    }

    /** Sends the email code without granting a logged-in session. */
    requestSSOEmailCode(token: string, email: string): Observable<TwoFaResponse> {
        return this.http.post<TwoFaResponse>(
            `${environment.apiUrl}/users/login/sso/request-email-code/`, {token, email}
        );
    }

    /** Stores login tokens only after the email challenge has been accepted. */
    verifySSOEmail(token: string, code: string): Observable<GenericResponse<AuthUser>> {
        return this.http.post<GenericResponse<AuthUser>>(
            `${environment.apiUrl}/users/login/sso/verify-email/`, {token, code}
        ).pipe(tap(r => {
            this.pendingSSOEmailVerification = undefined;
            this.storeLogin(r.data);
        }));
    }

    completeSSOSetup(): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}/users/complete-sso-setup/`, {});
    }

    private withExternalRecruitmentId(body: any): any {
        const externalRecruitmentId: string | null = this.recruitmentTrackingService.get();
        return externalRecruitmentId ? {...body, external_recruitment_id: externalRecruitmentId} : body;
    }

    checkUsername(username: string): Observable<UsernameSearch> {
        let params: HttpParams = new HttpParams();
        params = params.append('username', username);
        return this.http.get<UsernameSearch>(`${environment.apiUrl}/users/username-availability/`, {
            params
        });
    }
}
